import gamesService from '../services/gamesService';
import { processAuditDataManual } from './auditDataProcessor';
import showToast from './toastUtils';
import type { ActiveGamesAndPacksResponse } from '../services/authService';
import { buildGameEnrichmentMaps, normalizeIdToInt, normalizePriceToFloat } from './normalizers/auditTicketNormalizers';

/**
 * Filters out completed packs from previous audit data
 */
export const filterCompletedPacks = (prevAuditData: any[], gamesData: any[]): any[] => {
  if (!prevAuditData || !gamesData || prevAuditData.length === 0 || gamesData.length === 0) {
    return prevAuditData;
  }

  // Create a map of game_id to tickets_per_pack for quick lookup
  const gamesMap = new Map();
  gamesData.forEach((game: any) => {
    if (game.game_id && game.tickets_per_pack) {
      gamesMap.set(game.game_id.toString(), parseFloat(game.tickets_per_pack));
    }
  });

  // Filter out completed packs
  const filteredData = prevAuditData.filter((ticket: any) => {
    const gameId = ticket.game?.game_id?.toString() || ticket.game?.toString();
    const ticketsPerPack = gamesMap.get(gameId);
    
    if (!ticketsPerPack) {
      console.warn(`No tickets_per_pack found for game ID: ${gameId}, keeping ticket`);
      return true; // Keep if we can't determine pack size
    }

    // Check if this pack is completed (ending ticket number equals ticketsPerPack)
    let endingTicketNumber = 0;
    if (ticket.ending_ticket_number !== undefined) {
      endingTicketNumber = parseInt(ticket.ending_ticket_number.toString());
    } else if (ticket.range) {
      endingTicketNumber = parseInt(ticket.range?.split('-')[1] || '0');
    }
    
    const isCompleted = endingTicketNumber === ticketsPerPack;
    
    return !isCompleted; // Keep only incomplete packs
  });

  const removedCount = prevAuditData.length - filteredData.length;
  if (removedCount > 0) {
    
    // Prevent duplicate toast messages
    const toastMessage = `Removed ${removedCount} completed packs from previous audit data`;
    
    // Clear any pending toast timeout
    if (toastTimeoutId) {
      clearTimeout(toastTimeoutId);
      toastTimeoutId = null;
    }
    
    // Only show toast if it's a different message or enough time has passed
    if (lastToastMessage !== toastMessage) {
      toastTimeoutId = setTimeout(() => {
        showToast.info(toastMessage);
        lastToastMessage = toastMessage;
        toastTimeoutId = null;
      }, 200);
    }
  }

  return filteredData;
};

/**
 * Converts MostRecentAuditDetail to PreviousAuditDetail format
 */
export const convertToPreviousAuditDetail = (mostRecentDetails: any[]): any[] => {
  if (!mostRecentDetails || mostRecentDetails.length === 0) {
    return [];
  }

  return mostRecentDetails.map((detail: any) => ({
    game: {
      game_id: normalizeIdToInt(detail.game?.game_id ?? detail.game_id) ?? (detail.game?.game_id || detail.game_id),
      name: detail.game?.name || detail.name || '',
      cost_per_ticket: normalizePriceToFloat(detail.game?.cost_per_ticket) ?? (detail.game?.cost_per_ticket || 0),
      tickets_per_pack: normalizePriceToFloat(detail.game?.tickets_per_pack) ?? (detail.game?.tickets_per_pack || 0)
    },
    pack_id: normalizeIdToInt(detail.pack_id ?? detail.pack) ?? (detail.pack_id || detail.pack),
    starting_ticket_number: detail.starting_ticket_number || 0,
    ending_ticket_number: detail.ending_ticket_number || 0
  }));
};

/**
 * Fetches games and updates ticket details with prices and tickets per pack
 * @param ticketDetails - Array of ticket details to update
 * @param cachedGamesResponse - Optional cached games response to avoid duplicate API calls
 */
export const fetchGamesAndUpdateData = async (
  ticketDetails: any[],
  cachedGamesResponse?: any[],
  activeGamesAndPacks?: ActiveGamesAndPacksResponse
): Promise<{ updatedTicketDetails: any[], gamesResponse: any[] }> => {
  try {
    // Use cached games response if provided, otherwise fetch
    const gamesResponse = cachedGamesResponse || await gamesService.getAllGames();

    if (!gamesResponse || gamesResponse.length === 0) {
      return { updatedTicketDetails: ticketDetails, gamesResponse: [] };
    }

    const { costByGameId, ticketsPerPackByGameId } = buildGameEnrichmentMaps(
      gamesResponse,
      activeGamesAndPacks,
    );

    // Update ticket details with game prices
    const updatedTicketDetails = ticketDetails.map((ticket: any) => {
      const normalizedGameId = normalizeIdToInt(ticket.game);
      const gameId = normalizedGameId === null ? ticket.game?.toString() : String(normalizedGameId);
      const gamePrice = costByGameId.get(gameId);
      const ticketsPerPack = ticketsPerPackByGameId.get(gameId);
      
      if (!gamePrice) {
        console.warn(`No price found for game ID: ${gameId}`);
      }

      return {
        ...ticket,
        ticketPrice: normalizePriceToFloat(gamePrice) ?? 0,
        ticketsPerPack: normalizeIdToInt(ticketsPerPack) ?? 0
      };
    });

    const foundPrices = updatedTicketDetails.filter((t) => Number(t?.ticketPrice) > 0).length;
    const totalTickets = updatedTicketDetails.length;

    if (foundPrices < totalTickets) {
      showToast.warning(`${totalTickets - foundPrices} tickets could not be priced. Some games may not exist in the database.`);
    }

    return { updatedTicketDetails, gamesResponse };

  } catch (error) {
    console.error('Error fetching game prices:', error);
    showToast.error('Failed to fetch game prices. Using original data.');
    return { updatedTicketDetails: ticketDetails, gamesResponse: [] };
  }
};

// Track toast messages to prevent duplicates
let toastTimeoutId: NodeJS.Timeout | null = null;
let lastToastMessage: string | null = null;

/**
 * Processes ticket details with previous audit data
 */
export const processTicketDetailsWithPrevious = (
  currentTicketDetails: any[],
  previousAuditData: any[],
  gamesData: any[]
): any[] => {
  // Filter completed packs
  let filteredPrevAuditData = previousAuditData;
  if (gamesData && gamesData.length > 0 && filteredPrevAuditData.length > 0) {
    filteredPrevAuditData = filterCompletedPacks(filteredPrevAuditData, gamesData);
  }

  // Convert filtered data to the format expected by processAuditDataManual
  const convertedPrevAuditData = convertToPreviousAuditDetail(filteredPrevAuditData);
  
  // Process the data to get unified array with missing games, range issues, and new game packs
  const processed = processAuditDataManual(currentTicketDetails, convertedPrevAuditData);

  return processed && processed.length > 0 ? processed : currentTicketDetails;
};

const ACTIVE_GAME_PACK_ERROR_PREFIX = 'Invalid game or pack:';

const stripActiveGamePackError = (message: string | undefined): string => {
  if (!message) {
    return '';
  }

  return message
    .split('. ')
    .filter((part) => part && !part.startsWith(ACTIVE_GAME_PACK_ERROR_PREFIX))
    .join('. ')
    .trim();
};

export const validateTicketDetailsAgainstActiveGamesAndPacks = (
  ticketDetails: any[],
  activeGamesAndPacks: import('../services/authService').ActiveGamesAndPacksResponse | null | undefined
): {
  ticketDetails: any[];
  invalidGames: Array<{ game: string | number; pack?: string | number; range?: string }>;
  invalidPacks: Array<{ game: string | number; pack: string | number; range?: string }>;
} => {
  if (!Array.isArray(ticketDetails) || ticketDetails.length === 0) {
    return {
      ticketDetails,
      invalidGames: [],
      invalidPacks: [],
    };
  }

  if (!activeGamesAndPacks || activeGamesAndPacks.length === 0) {
    return {
      ticketDetails,
      invalidGames: [],
      invalidPacks: [],
    };
  }

  const activeGameIds = new Set<string>();
  const activeGamePackMap = new Map<string, Set<string>>();
  const packStatusMap = new Map<string, string>();

  activeGamesAndPacks.forEach((game) => {
    const gameId = String(game.game_id);
    activeGameIds.add(gameId);

    const packSet = new Set<string>();
    game.packs.forEach((pack) => {
      const packId = String(pack.pack_id);
      packSet.add(packId);
      packStatusMap.set(`${gameId}-${packId}`, pack.status || 'activated');
    });
    activeGamePackMap.set(gameId, packSet);
  });

  const invalidGames: Array<{ game: string | number; pack?: string | number; range?: string }> = [];
  const invalidPacks: Array<{ game: string | number; pack: string | number; range?: string }> = [];

  const validatedTicketDetails = ticketDetails.map((ticket: any) => {
    const game = ticket.game ?? ticket.game_id;
    const pack = ticket.pack ?? ticket.pack_id;
    const sanitizedErrorMessage = stripActiveGamePackError(ticket.errorMessage);

    const baseTicket = {
      ...ticket,
      errorMessage: sanitizedErrorMessage,
      haveError: ticket.haveError && Boolean(sanitizedErrorMessage),
    };

    if (game == null || game === '') {
      return {
        ...baseTicket,
        pack_status: undefined,
      };
    }

    const gameId = String(game);

    if (!activeGameIds.has(gameId)) {
      const invalidGameMessage = `${ACTIVE_GAME_PACK_ERROR_PREFIX} game ${gameId} is not active for this store`;
      invalidGames.push({
        game: gameId,
        pack,
        range: ticket.range,
      });

      return {
        ...baseTicket,
        haveError: true,
        errorMessage: sanitizedErrorMessage
          ? `${sanitizedErrorMessage}. ${invalidGameMessage}`
          : invalidGameMessage,
        pack_status: 'Invalid',
      };
    }

    if (pack == null || pack === '') {
      return {
        ...baseTicket,
        pack_status: undefined,
      };
    }

    const packId = String(pack);
    const validPacksForGame = activeGamePackMap.get(gameId);
    const packKey = `${gameId}-${packId}`;

    if (!validPacksForGame || !validPacksForGame.has(packId)) {
      const invalidPackMessage = `${ACTIVE_GAME_PACK_ERROR_PREFIX} game ${gameId} pack ${packId} is not active for this store`;
      invalidPacks.push({
        game: gameId,
        pack: packId,
        range: ticket.range,
      });

      return {
        ...baseTicket,
        haveError: true,
        errorMessage: sanitizedErrorMessage
          ? `${sanitizedErrorMessage}. ${invalidPackMessage}`
          : invalidPackMessage,
        pack_status: 'Invalid',
      };
    }

    return {
      ...baseTicket,
      pack_status: packStatusMap.get(packKey) || 'activated',
    };
  });

  return {
    ticketDetails: validatedTicketDetails,
    invalidGames,
    invalidPacks,
  };
};

/**
 * Marks ticket details whose game-pack combination does not exist
 * in the store's active games and packs list.
 */
export const markInvalidGamePacksUsingActivatedPacks = (
  ticketDetails: any[],
  activeGamesAndPacks: import('../services/authService').ActiveGamesAndPacksResponse | null | undefined
): any[] => {
  return validateTicketDetailsAgainstActiveGamesAndPacks(
    ticketDetails,
    activeGamesAndPacks
  ).ticketDetails;
};
